// useSpotlight.ts - Hook for calculating spotlight and tooltip positions
import { useState, useEffect, useCallback, useRef } from 'react';

export interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

export interface TooltipPosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom' | 'left' | 'right';
  arrowPosition: number; // Percentage along the edge for arrow placement
}

interface UseSpotlightOptions {
  targetSelector: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  padding?: number;
  tooltipWidth?: number;
  tooltipHeight?: number;
  offset?: number; // Distance between spotlight and tooltip
}

interface UseSpotlightReturn {
  targetRect: SpotlightRect | null;
  tooltipPosition: TooltipPosition | null;
  targetElement: HTMLElement | null;
  scrollToTarget: () => void;
  recalculate: () => void;
}

const DEFAULT_PADDING = 8;
const DEFAULT_TOOLTIP_WIDTH = 320;
const DEFAULT_TOOLTIP_HEIGHT = 180;
const DEFAULT_OFFSET = 128;
const VIEWPORT_MARGIN = 16;

export function useSpotlight({
  targetSelector,
  placement = 'auto',
  padding = DEFAULT_PADDING,
  tooltipWidth = DEFAULT_TOOLTIP_WIDTH,
  tooltipHeight = DEFAULT_TOOLTIP_HEIGHT,
  offset = DEFAULT_OFFSET,
}: UseSpotlightOptions): UseSpotlightReturn {
  const [targetRect, setTargetRect] = useState<SpotlightRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const recalculateRef = useRef<() => void>(() => {});

  const calculatePositions = useCallback(() => {
    // Guard against empty selector
    if (!targetSelector) {
      setTargetRect(null);
      setTooltipPosition(null);
      setTargetElement(null);
      return;
    }

    // Find target element
    const element = document.querySelector(targetSelector) as HTMLElement | null;
    setTargetElement(element);

    if (!element) {
      setTargetRect(null);
      setTooltipPosition(null);
      console.warn(`[useSpotlight] Element not found for: ${targetSelector}`);
      return;
    }

    // Get element's bounding rect
    const rect = element.getBoundingClientRect();

    // Debug logging
    console.log(`[useSpotlight] Found element for "${targetSelector}":`, {
      tagName: element.tagName,
      className: element.className?.substring(0, 50),
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    });

    // Calculate spotlight rect with padding
    const spotlight: SpotlightRect = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      bottom: rect.bottom + padding,
      right: rect.right + padding,
    };
    setTargetRect(spotlight);

    // Calculate best placement if auto
    let finalPlacement = placement;
    if (placement === 'auto') {
      finalPlacement = calculateBestPlacement(rect, tooltipWidth, tooltipHeight, offset);
    }

    // Calculate tooltip position
    const tooltip = calculateTooltipPosition(
      rect,
      finalPlacement as 'top' | 'bottom' | 'left' | 'right',
      tooltipWidth,
      tooltipHeight,
      offset
    );
    setTooltipPosition(tooltip);
  }, [targetSelector, placement, padding, tooltipWidth, tooltipHeight, offset]);

  // Store recalculate function in ref for external access
  recalculateRef.current = calculatePositions;

  const scrollToTarget = useCallback(() => {
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
      // Recalculate after scroll animation
      setTimeout(calculatePositions, 400);
    }
  }, [targetElement, calculatePositions]);

  // Calculate positions on mount and when selector changes
  useEffect(() => {
    // Clear previous state immediately when selector changes
    // This prevents showing stale positions from previous step
    setTargetRect(null);
    setTooltipPosition(null);
    setTargetElement(null);

    // Guard against empty selector - state is already cleared above
    if (!targetSelector) {
      return;
    }

    let frameId: number;
    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 50; // ms between retries

    // Function to attempt finding and calculating position
    const attemptCalculation = () => {
      const element = document.querySelector(targetSelector) as HTMLElement | null;

      if (element) {
        // Element found - calculate positions
        calculatePositions();
      } else if (retryCount < maxRetries) {
        // Element not found yet - retry after a short delay
        retryCount++;
        frameId = requestAnimationFrame(() => {
          setTimeout(attemptCalculation, retryDelay);
        });
      } else {
        // Max retries reached - element probably doesn't exist
        console.warn(`[useSpotlight] Could not find element for selector: ${targetSelector}`);
      }
    };

    // Use double requestAnimationFrame to ensure DOM is fully rendered
    // after React's batched updates complete
    frameId = requestAnimationFrame(() => {
      requestAnimationFrame(attemptCalculation);
    });

    // Recalculate on resize and scroll
    const handleResize = () => calculatePositions();
    const handleScroll = () => calculatePositions();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    // Use ResizeObserver for element size changes
    const resizeObserver = new ResizeObserver(calculatePositions);

    // Set up observer after element is found
    const setupObserver = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        resizeObserver.observe(element);
      }
    };

    // Delay observer setup to ensure element exists
    const observerTimeout = setTimeout(setupObserver, 100);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(observerTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      resizeObserver.disconnect();
    };
  }, [targetSelector, calculatePositions]);

  return {
    targetRect,
    tooltipPosition,
    targetElement,
    scrollToTarget,
    recalculate: calculatePositions,
  };
}

function calculateBestPlacement(
  rect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
  offset: number
): 'top' | 'bottom' | 'left' | 'right' {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Calculate available space in each direction
  const spaceTop = rect.top - VIEWPORT_MARGIN;
  const spaceBottom = viewportHeight - rect.bottom - VIEWPORT_MARGIN;
  const spaceLeft = rect.left - VIEWPORT_MARGIN;
  const spaceRight = viewportWidth - rect.right - VIEWPORT_MARGIN;

  const neededVertical = tooltipHeight + offset;
  const neededHorizontal = tooltipWidth + offset;

  // Priority: bottom > top > right > left
  if (spaceBottom >= neededVertical) return 'bottom';
  if (spaceTop >= neededVertical) return 'top';
  if (spaceRight >= neededHorizontal) return 'right';
  if (spaceLeft >= neededHorizontal) return 'left';

  // Fallback to the direction with most space
  const spaces = [
    { placement: 'bottom' as const, space: spaceBottom },
    { placement: 'top' as const, space: spaceTop },
    { placement: 'right' as const, space: spaceRight },
    { placement: 'left' as const, space: spaceLeft },
  ];

  return spaces.sort((a, b) => b.space - a.space)[0].placement;
}

function calculateTooltipPosition(
  rect: DOMRect,
  placement: 'top' | 'bottom' | 'left' | 'right',
  tooltipWidth: number,
  tooltipHeight: number,
  offset: number
): TooltipPosition {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top: number;
  let left: number;
  let arrowPosition = 50; // Default center

  switch (placement) {
    case 'top':
      top = rect.top - tooltipHeight - offset;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'bottom':
      top = rect.bottom + offset;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - offset;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + offset;
      break;
  }

  // Constrain to viewport with margin
  if (placement === 'top' || placement === 'bottom') {
    // Horizontal constraint
    if (left < VIEWPORT_MARGIN) {
      const shift = VIEWPORT_MARGIN - left;
      left = VIEWPORT_MARGIN;
      arrowPosition = Math.max(10, 50 - (shift / tooltipWidth) * 100);
    } else if (left + tooltipWidth > viewportWidth - VIEWPORT_MARGIN) {
      const shift = left + tooltipWidth - (viewportWidth - VIEWPORT_MARGIN);
      left = viewportWidth - VIEWPORT_MARGIN - tooltipWidth;
      arrowPosition = Math.min(90, 50 + (shift / tooltipWidth) * 100);
    }
  } else {
    // Vertical constraint
    if (top < VIEWPORT_MARGIN) {
      const shift = VIEWPORT_MARGIN - top;
      top = VIEWPORT_MARGIN;
      arrowPosition = Math.max(10, 50 - (shift / tooltipHeight) * 100);
    } else if (top + tooltipHeight > viewportHeight - VIEWPORT_MARGIN) {
      const shift = top + tooltipHeight - (viewportHeight - VIEWPORT_MARGIN);
      top = viewportHeight - VIEWPORT_MARGIN - tooltipHeight;
      arrowPosition = Math.min(90, 50 + (shift / tooltipHeight) * 100);
    }
  }

  return {
    top,
    left,
    placement,
    arrowPosition,
  };
}

export default useSpotlight;
